// Rect2D.js
// Author: Dave Fuhry <dfuhry@cs.kent.edu>
// Copyright 2007, Data Cache Ohio

class Rect2D {

    constructor(minx, miny, maxx, maxy) {
        this.screenPxPerMeter = 72.0 * 39.3700787;
        if (minx instanceof Rect2D)
        {
            this.setXCoords(minx.minx, minx.maxx);
            this.setYCoords(minx.miny, minx.maxy);
        } else if (minx instanceof Array) {
            if (minx.length === 4)
            {
                this.setXCoords(minx[0], minx[2]);
                this.setYCoords(minx[1], minx[3]);
            }
        } else {
            if (minx != null && maxx != null)
            {
                this.setXCoords(minx, maxx);
            }
            if (miny != null && maxy != null)
            {
                this.setYCoords(miny, maxy);
            }
        }
    }

    setXCoords(x1, x2) {
        this.minx = Math.min(x1, x2);
        this.maxx = Math.max(x1, x2);
    }

    setYCoords(y1, y2) {
        this.miny = Math.min(y1, y2);
        this.maxy = Math.max(y1, y2);
    }

    incrementXBy(x) {
        this.minx += x;
        this.maxx += x;
    }

    incrementYBy(y) {
        this.miny += y;
        this.maxy += y;
    }

    getWidth() {
        return this.maxx - this.minx;
    }

    getHeight() {
        return this.maxy - this.miny;
    }

    getProjectionOf(rect2d, r2dMaxWidth, r2dMaxHeight) {
        let minx = this.minx + (this.getWidth() * rect2d.minx / r2dMaxWidth);
        let miny = this.miny + (this.getHeight() * (r2dMaxHeight - rect2d.maxy) / r2dMaxHeight);
        let maxx = this.minx + (this.getWidth() * rect2d.maxx / r2dMaxWidth);
        let maxy = this.miny + (this.getHeight() * (r2dMaxHeight - rect2d.miny) / r2dMaxHeight);
        return new Rect2D(minx, miny, maxx, maxy);
    }

    positionElemAt(elem, offsetX, offsetY) {
        let eStyle = elem.style;
        eStyle.left = (this.minx + offsetX) + 'px';
        eStyle.top = (this.miny + offsetY) + 'px';
        eStyle.width = (this.getWidth()) + 'px';
        eStyle.height = (this.getHeight()) + 'px';
    }

    getProportionalTo(imgWidth, imgHeight) {
        let imgProp = imgWidth / imgHeight;
        let thisWidth = this.getWidth();
        let thisHeight = this.getHeight();
        let boxProp = thisWidth / thisHeight;
        let pWidth = thisWidth;
        let pHeight = thisHeight;
        if (boxProp > imgProp) {
            pHeight = thisWidth / imgProp;
        } else {
            pWidth = thisHeight * imgProp;
        }
        let minx = this.minx + ((thisWidth - pWidth) / 2.0);
        let miny = this.miny + ((thisHeight - pHeight) / 2.0);
        return new Rect2D(minx, miny, minx + pWidth, miny + pHeight);
    }

    getCenter() {
        return [this.getCenterX(), this.getCenterY()];
    }

    getCenterX() {
        return this.minx + ((this.maxx - this.minx) / 2.0);
    }

    getCenterY() {
        return this.miny + ((this.maxy - this.miny) / 2.0);
    }

    scaleBy(sf) {
        let c = this.getCenter();
        let cDistX = this.getWidth() / 2.0 * sf;
        let cDistY = this.getHeight() / 2.0 * sf;
        return new Rect2D(c[0] - cDistX, c[1] - cDistY, c[0] + cDistX, c[1] + cDistY);
    }

    getScaleDenom(imageWidth) {
        // TODO: fix meter-assumption
        return this.getWidth() * this.screenPxPerMeter / parseFloat(imageWidth);
    }

    getWPxAtScaleDenom(sd) {
        return this.getWidth() * this.screenPxPerMeter / sd;
    }

    toString() {
        return '(' + this.minx.toFixed(4) + ', ' + this.miny.toFixed(4) + '), (' + this.maxx.toFixed(4) + ', ' + this.maxy.toFixed(4) + ')';
    }

    toMapExtString() {
        return this.minx.toFixed(6) + ' ' + this.miny.toFixed(6) + ' ' + this.maxx.toFixed(6) + ' ' + this.maxy.toFixed(6);
    }

    toWKT() {
        let wkt;
        if (this.minx === this.maxx && this.miny === this.maxy)
        {
            wkt = 'POINT(' + this.minx + ' ' + this.miny + ')';
        } else {
            wkt = 'POLYGON((' + this.minx + ' ' + this.miny + ',' + this.minx + ' ' + this.maxy + ',' + this.maxx + ' ' + this.maxy + ',' + this.maxx + ' ' + this.miny + ',' + this.minx + ' ' + this.miny + '))';
        }
        return wkt;
    }

    intersects(r) {
        return (r.minx < this.maxx && r.miny < this.maxy && r.maxx > this.minx && r.maxy > this.miny);
    }

    scaleOrdsBy(s) {
        this.minx *= s;
        this.miny *= s;
        this.maxx *= s;
        this.maxy *= s;
    }
}

export default Rect2D;